/* eslint-disable no-undef */
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import Cookies from 'universal-cookie';
import appService from './appService';
import authHeader from './authHeader';
import {
  POLICIES_DEVIATIONS,
  POLICY_TYPE_ID,
  REPORT_SIZE,
  REPORT,
  MICROSOFT_LOGIN,
} from './constants';

const API_URL = '/api/v1/';
const TOS_API_URL = '/tos/v1/';
const controller = new AbortController();

let isRefreshing = false;
let isUserTOS = false;

axios.interceptors.request.use(
  async config => {
    try {
      const cookies = new Cookies();
      const cookie = cookies.get('nic-portal') || cookies.get('nic-tos');
      if (cookie) {
        const decodedToken = jwt_decode(cookie);
        const expirationTime = decodedToken.exp * 1000 - 120000;
        const currentTime = new Date().getTime();

        if (expirationTime < currentTime) {
          if (!isRefreshing) {
            isRefreshing = true;
            const { data } = await refreshToken();
            appService.setCookie(data.accessToken);
          }
        }
      }
    } catch (error) {
      // Handle any errors that occur during token refresh
      console.error('Error refreshing token:', error);
    } finally {
      isRefreshing = false;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    const statusCode = error.response ? error.response.status : null;
    if (statusCode === 401) {
      appService.logout();
    } else if (statusCode === 500) {
      // throw new Error('Server is down !');
      console.error('Server is down !');
    }

    return Promise.reject(error);
  }
);

const refreshToken = async () => {
  return await axios.get(isUserTOS ? TOS_API_URL + 'refresh' : API_URL + 'refresh', {
    headers: authHeader(),
    withCredentials: true,
  });
};

const structuredChartData = async state => {
  return await axios.get(API_URL + 'identify/summary' + state, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const lineChartData = async state => {
  return await axios.get(API_URL + 'identify/trend' + state, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const TableData = async state => {
  return await axios.get(API_URL + 'identify/source' + state, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const getMessages = () => {
  return axios.get(API_URL + 'message', {
    headers: authHeader(),
    withCredentials: true,
  });
};

const getMessageBody = msgId => {
  return axios.get(API_URL + 'message/' + msgId, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const setMessage = (msgId, stat) => {
  return axios.patch(API_URL + 'message/' + msgId + '/?read=' + stat, {
    // params: {
    //   read: stat,
    // },
    headers: authHeader(),
    withCredentials: true,
  });
};

const getUnreadMessages = stat => {
  return axios.get(API_URL + 'message/?read=' + stat, {
    headers: authHeader(),
    withCredentials: true,
  });
};

// (DELETE) http://url.com/api/v1/message/42
const deleteMessage = msgId => {
  return axios.delete(API_URL + 'message/' + msgId, {
    headers: authHeader(),
    withCredentials: true,
  });
};

// (GET) http://url.com/api/v1/account
const getProfile = () => {
  return axios.get(API_URL + 'account', {
    headers: authHeader(),
    withCredentials: true,
  });
};

// (POST) http://url.com/api/v1/account
const setProfile = newProf => {
  return axios.post(API_URL + 'account', newProf, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const getSettings = () => {
  return axios.get(API_URL + 'settings', {
    headers: authHeader(),
    withCredentials: true,
  });
};

const getUsers = async () => {
  return axios.get(
    API_URL + 'users',
    {
      headers: authHeader(),
      withCredentials: true,
    },
    setTimeout(() => {
      controller.abort();
    }, 2000)
  );
};

const setUser = async info => {
  const { data } = await axios.post(API_URL + 'users', info, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const getRoles = async () => {
  const { data } = await axios.get(API_URL + 'roles', {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const getAuthorities = async () => {
  const { data } = await axios.get(API_URL + 'authz', {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const deleteUser = userId => {
  return axios.delete(API_URL + 'users/' + userId, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const getMainChart = () => {
  return axios.get(API_URL + 'chart', {
    headers: authHeader(),
    withCredentials: true,
  });
};

const fetchOrgInfo = () => {
  const response = axios.get(API_URL + 'customer/info', {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const validateJwtToken = jwtStr => {
  return axios.post('/validate', jwtStr, { headers: authHeader() });
};

const updateUser = async info => {
  const { data } = await axios.patch(API_URL + 'users', info, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const saveSettings = async info => {
  const { data } = await axios.post(API_URL + 'settings', info, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const fetchVersionInfo = async () => {
  const response = await axios.get(API_URL + 'versions', {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const deleteMultipleMessage = payload => {
  return axios.delete(API_URL + 'message', {
    headers: authHeader(),
    withCredentials: true,
    data: payload,
  });
};

const updateMultipleMessage = msgId => {
  return axios.patch(API_URL + 'message', msgId, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const getLoginData = async () => {
  const { data } = await axios.get(API_URL + 'logindata', {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const addMilestones = async payload => {
  const { data } = await axios.post(API_URL + 'identify/milestonev2', payload, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const getMilestones = async () => {
  const { data } = await axios.get(API_URL + 'identify/milestonev2', {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const updateMilestone = (payload, milestoneId) => {
  return axios.patch(API_URL + 'identify/milestonev2/' + milestoneId, payload, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const deleteMilestone = milestoneId => {
  return axios.delete(API_URL + 'identify/milestonev2/' + milestoneId, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const fetchRss = async date => {
  const { data } = await axios.get(API_URL + 'rss?url=0&from=' + date, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const fetchCert = async date => {
  const { data } = await axios.get(API_URL + 'rss?url=certse&from=' + date, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};
const fetchEnisa = async date => {
  const { data } = await axios.get(API_URL + 'rss?url=enisa&from=' + date, {
    headers: authHeader(),
    withCredentials: true,
  });
  return data;
};

const fetchPolicyTypes = async id => {
  const response = await axios.get(API_URL + 'policies/types/' + id, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchAllPolicies = async () => {
  const response = await axios.get(API_URL + 'policies/policy', {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchAllPolicyTypes = async () => {
  const response = await axios.get(API_URL + 'policies/types', {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const createPolicyWithoutTemplate = async payload => {
  const response = await axios.post(API_URL + 'policies/policy', payload, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const createPolicyWithTemplate = async payload => {
  const response = await axios.post(API_URL + 'policies/policy', payload, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const deletePolicyCompletelyByID = async id => {
  const response = await axios.delete(API_URL + 'policies/policy/' + id + '?fromStorage=true', {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const deletePolicyCacheByID = async id => {
  const response = await axios.delete(API_URL + 'policies/policy/' + id, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const savePolicy = async id => {
  const response = await axios.put(API_URL + 'policies/policy/' + id, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const savePolicyNameAndComment = async data => {
  const response = await axios.patch(API_URL + 'policies/policy/' + data.id, {
    ...data.data,
  });
  return response;
};

const getPolicyById = async ({ id, readOnly }) => {
  const policyId = id ? id : POLICY_TYPE_ID;
  const response = await axios.get(API_URL + 'policies/policy/' + policyId, {
    headers: authHeader(),
    params: { readOnly: readOnly ? 'true' : 'false' },
    withCredentials: true,
  });
  return response;
};

const getPolicyByDate = async policyTypeAndDate => {
  const response = await axios.get(API_URL + 'policies/activated/' + policyTypeAndDate, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const activatePolicy = async (id, payload) => {
  const response = await axios.patch(API_URL + 'policies/policy/' + id, payload, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const createNewDataClassLevel = async payload => {
  const response = await axios.post(API_URL + 'policies/classlevel/', payload, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const dragDropItems = async (id, payload) => {
  const response = await axios.patch(API_URL + 'policies/classlevel/' + id, payload, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const repositionColumns = async payload => {
  const response = await axios.patch(API_URL + 'policies/classlevel/' + payload.id, payload.obj, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const manageProtectionAspects = async payload => {
  const response = await axios.patch(
    API_URL + 'policies/classlevel/' + payload?.id,
    payload?.data,
    {
      headers: authHeader(),
      withCredentials: 'true',
    }
  );
  return response;
};

const deleteDataClassLevel = async id => {
  const response = await axios.delete(API_URL + 'policies/classlevel/' + id, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const updateNameOrDescDataColumns = async payload => {
  const response = await axios.patch(API_URL + 'policies/classlevel/' + payload?.id, payload.obj, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
};

const updateUserSettings = async payload => {
  const response = await axios.post(API_URL + 'settings', payload, {
    headers: authHeader(),
    withCredentials: 'true',
  });
  return response;
}

const requestDataType = dataType => {
  return axios.post(API_URL + 'policies/informationobject/new', dataType, {
    headers: authHeader(),
    withCredentials: true,
  });
};

const fetchComplianceData = async (policyTypeId, query) => {
  const response = await axios.get(
    API_URL + 'policies/deviations/' + policyTypeId + '/compliance/' + query,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchTrendsData = async (policyTypeId, query) => {
  const response = await axios.get(
    API_URL + 'policies/deviations/' + policyTypeId + '/trend/' + query,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchObjectsAndDeviations = async (policyTypeId, query) => {
  const response = await axios.get(
    API_URL + 'policies/deviations/' + policyTypeId + '/summed/' + query,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchDeviationsPerDataSource = async (policyTypeId, data) => {
  const response = await axios.post(
    API_URL + 'policies/deviations/' + policyTypeId + '/datasource',
    data,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchDeviationsPerInfoType = async (policyTypeId, data) => {
  const response = await axios.post(
    API_URL + 'policies/deviations/' + policyTypeId + '/level',
    data,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchEstimatedDeviationsReportSize = async data => {
  const response = await axios.post(
    API_URL + POLICIES_DEVIATIONS + POLICY_TYPE_ID + REPORT_SIZE,
    data,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchExportDeviationsReportRequest = async data => {
  const response = await axios.post(API_URL + POLICIES_DEVIATIONS + POLICY_TYPE_ID + REPORT, data, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const microsoftOIDCLogin = async () => {
  const response = await axios.get(MICROSOFT_LOGIN);
  return response;
};

const swapToken = async (orgId) => {
  const response = await axios.get(API_URL + 'refresh/' + orgId, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
}

const fetchAllFrameworks = async lang => {
  const response = await axios.get(API_URL + `qsrvc/frameworks` + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchAllSections = async (fwId, lang) => {
  const response = await axios.get(API_URL + `qsrvc/framework/${fwId}` + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchAllQuestions = async (fwId, lang) => {
  const response = await axios.get(API_URL + 'qsrvc/fwquestions/' + fwId + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchAllAnswers = async (fwId, lang) => {
  const response = await axios.get(API_URL + 'qsrvc/fwanswers/' + fwId + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchQuestions = async (fwId, sectId, lang) => {
  const response = await axios.get(
    API_URL + 'qsrvc/questions/' + fwId + '/' + sectId + '?lang=' + lang,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const saveQuestionResponse = async data => {
  const response = await axios.post(
    isUserTOS ? TOS_API_URL + 'qsrvc/question/response' : API_URL + 'qsrvc/question/response',
    data,
    {
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchAnswers = async (fwId, sectId) => {
  const response = await axios.get(API_URL + 'qsrvc/answers/' + fwId + '/' + sectId, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchFrameworksStats = async ( lang ) => {
  const response = await axios.get(API_URL + 'qsrvc/statistics' + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const getFramworkAnswersSummary = async (fwId, lang) => {
  const response = await axios.get(API_URL + 'qsrvc/fwsummary/' + fwId + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const getAutoRecommendation = async (fwId, sectId, lang, onMessageCallback) => {
  const eventSource = new EventSource(
    API_URL + 'qsrvc/recommend/' + fwId + '/' + sectId + '?lang=' + lang
  );

  eventSource.onopen = () => {};

  eventSource.onmessage = event => {
    onMessageCallback(event.data);
  };

  eventSource.addEventListener('end', () => {
    eventSource.close();
  });

  eventSource.onerror = () => {
    // Handle error, clear content (?)
  };
};

const downloadFile = async (fileObjectId) => {
  const response = await axios.get(API_URL + 'download/' + fileObjectId, {
    headers: authHeader(),
    withCredentials: true,
    responseType: 'blob', // blob response type is needed to correctly display/download the files received
  });
  return response;
} 

const createTOSUrl = async data => {
  const response = await axios.post(API_URL + 'qsrvc/tos', data, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const validateTOSCode = async (pathId, TOScode) => {
  const response = await axios.post(
    'tosv/',
    { id: pathId, code: TOScode },
    {
      // Correct url?
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response;
};

const fetchTOSQuestions = async lang => {
  const response = await axios.get(TOS_API_URL + 'qsrvc/questions' + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const fetchTOSAnswers = async lang => {
  const response = await axios.get(TOS_API_URL + 'qsrvc/answers' + '?lang=' + lang, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response;
};

const setIsUserTOS = value => {
  isUserTOS = value;
  appService.setIsUserTOS(value);
};

export default {
  // == Messages ==
  getMessages,
  getMessageBody,
  setMessage,
  deleteMessage,
  getUnreadMessages,
  // == Profile ==
  getProfile,
  setProfile,
  // == Settings ==
  getSettings,
  // == Users ==
  setUser,
  getUsers,
  deleteUser,
  // == Charts ==
  getMainChart,
  // == Organisation ==
  fetchOrgInfo,
  // == Other ==
  validateJwtToken,
  microsoftOIDCLogin,
  swapToken,
  getRoles,
  getAuthorities,
  updateUser,
  saveSettings,
  fetchVersionInfo,
  deleteMultipleMessage,
  updateMultipleMessage,
  getLoginData,
  refreshToken,
  structuredChartData,
  lineChartData,
  TableData,
  addMilestones,
  getMilestones,
  updateMilestone,
  deleteMilestone,
  fetchRss,
  fetchEnisa,
  fetchCert,
  fetchPolicyTypes,
  fetchAllPolicies,
  fetchAllPolicyTypes,
  createPolicyWithoutTemplate,
  createPolicyWithTemplate,
  deletePolicyCompletelyByID,
  savePolicy,
  getPolicyById,
  activatePolicy,
  createNewDataClassLevel,
  dragDropItems,
  repositionColumns,
  deletePolicyCacheByID,
  manageProtectionAspects,
  deleteDataClassLevel,
  updateNameOrDescDataColumns,
  updateUserSettings,
  getPolicyByDate,
  savePolicyNameAndComment,
  requestDataType,
  downloadFile,
  // == Compliance ==
  fetchComplianceData,
  fetchTrendsData,
  fetchObjectsAndDeviations,
  fetchDeviationsPerDataSource,
  fetchDeviationsPerInfoType,
  fetchEstimatedDeviationsReportSize,
  fetchExportDeviationsReportRequest,
  // == GAP analysis ==
  fetchAllFrameworks,
  fetchAllSections,
  fetchAllQuestions,
  fetchAllAnswers,
  fetchQuestions,
  saveQuestionResponse,
  fetchAnswers,
  fetchFrameworksStats,
  getFramworkAnswersSummary,
  getAutoRecommendation,
  // == TOS ==
  createTOSUrl,
  validateTOSCode,
  fetchTOSQuestions,
  fetchTOSAnswers,
  setIsUserTOS,
};
